import { formApi } from "@/api/form";
import { programsApi } from "@/api/programs";
import { replace } from "@/utils/vue";
import { defineStore } from "pinia";
import { computed, reactive, ref } from "vue";

type Team = {
  id: string, 
  name: string, 
  trackerId: string, 
  supervisorId: string,
  programStageId: string,
  reports?: any,
  limits?: any,
  lastReport?: any,
  checklist?: any,
  TrackerReport: any
}

type Program = {
  id: string,
  name: string,  
  students?: { id: string, name: string, surname: string, avatar: string, teamId: string }[]
  teams: Team[],
  analitics?: any,
  transfers?: { studentId: string, teamId: string, newTeamId: string, date: string }[]
}

type Stage = {
  id: string,
  name: string,
  deadline: string
}

export const useProgramsStore = defineStore('data', () => {

  const programs = reactive<Program[]>([])
  const stages = reactive<Stage[]>([])
  const status = ref<"init" | "loaded">("init")
  const limitsMeta = reactive<{ title: string, category: string, fields: any }[]>([])

  const teams = computed(() => {
    const arr = []
    for (let program of programs) {
      arr.push(...program.teams)
    }
    return arr
  })

  const init = async () => {
    const resp = await programsApi.getAll()
    console.log(resp)
    replace(programs, resp)

    const _stages = await programsApi.getStages()
    console.log(_stages)
    replace(stages, _stages)
    status.value = "loaded"

    const _limitMeta = await formApi.getLimitsMeta()
    replace(limitsMeta, _limitMeta)
  }

  const requestStudents = async (programId: string) => {
    const program = programs.find(p => p.id === programId)!
    program.students = await programsApi.getStudents(programId)
  }

  const requestReports = async (team: Team) => {
    const { reports, limits } = await programsApi.getReports(team.id)
    team.reports = reports
    team.limits = limits
  }
  const requestLastReport = async (team: Team) => {
    const { lastReport, checklist } = await formApi.getCheckList(team.id)
    team.lastReport = lastReport
    const set = new Set(team.lastReport?.stageTasks.map((item: any) => item.id) || [])
    for (let checkbox of checklist) {
      if (set.has(checkbox.id)) checkbox.checked = true
    }
    team.checklist = {
      quantity: checklist.filter((item: any) => item.type === 'quantity'),
      quality: checklist.filter((item: any) => item.type === 'quality')
    }
  }

  const requestAnalitics = async (program: Program) => {
    if (program.analitics) return
    const analitics = await programsApi.getAnalitics(program.id)
    program.analitics = analitics
  }

  const fillArray = (formData: FormData, students: any[]) => {
    for (let student of students) {
      if (student.avatar && student.avatar.file) {
        formData.append(student.id+'@avatar', student.avatar.file)
      }
      const names = student.name.trim().split(" ")
      if (names.length === 1) {
        formData.append(student.id+'@name', student.name)
      } else {
        formData.append(student.id+'@name', names.slice(0, names.length-1).join(" "))
        formData.append(student.id+'@surname', names[names.length-1])
      }
    }
  }

  const addProgram = async (name: string, students: { id: string, name: string, avatar?: { file?: File } }[]) => {

    const formData = new FormData()
    formData.append('name', name)
    fillArray(formData, students)

    const resp = await programsApi.addProgram(formData)
    programs.push(resp)
  }

  const addTeam = async (programId: string, values: any) => {
    const resp = await programsApi.addTeam(programId, values)
    const program = programs.find(p => p.id === programId)!
    program.teams.push(resp)
  }

  const editProgram = async (programId: string, name: string, students: { id: string, name: string, avatar?: { file?: File } }[]) => {
    const formData = new FormData()
    formData.append('name', name)
    fillArray(formData, students) 
    const resp = await programsApi.editProgram(programId, formData)
    const program = programs.find(item => item.id === programId)!
    program.name = name
  }

  const editTeam = async (teamId: string, programId: string, values: any) => {
    const resp = await programsApi.editTeam(teamId, programId, values)
    const program = programs.find(p => p.id === programId)!
    const team = program.teams.find(item => item.id === teamId)!
    Object.assign(team, values)
  }

  const completeReportRequest = async (report: any) => {
    report.changeStageRequest = false
    const program = programs.find(item => item.id === report.programId)!
    const team = program.teams.find(item => item.id === report.teamId)!
    team.TrackerReport = null
    team.programStageId = report.newProgramStageId
    await programsApi.completeReportRequest(report.id)
  }

  const cancelReportRequest = async (report: any) => {
    report.changeStageRequest = false
    await programsApi.cancelReportRequest(report.id)
  }

  const checklists = reactive<{ [key: string]: { 
    quantity: { title: string, id: string }[], 
    quality: { title: string, id: string }[],
    all: { title: string, id: string, type: "quality" | "quantity" }[]
  }}>({})
  const requestChecklist = async () => {
    if (Object.keys(checklists).length > 0) return
    const resp = await formApi.getAllCheckLists()
    replace(checklists, {})
    for (let checkbox of resp) {
        const task = checklists[checkbox.programStageId] || { quantity: [], quality: [], all: [] }
        task[checkbox.type as "quantity" | "quality"].push({ id: checkbox.id, title: checkbox.title })
        checklists[checkbox.programStageId] = task
        task.all.push({ id: checkbox.id, title: checkbox.title, type: checkbox.type })
    } 
  }

  const getStage = (stageId: string) => {
    if (!stageId) return null
    return stages.find(item => item.id === stageId)
  }

  const requestTransfers = async (program: Program) => {
    const resp = await programsApi.getTransfers(program.id)
    program.transfers = resp.transfers
  }

  return {
    programs,
    stages,
    teams,
    status,
    checklists,
    limitsMeta,
    getStage,
    init,
    requestStudents,
    requestAnalitics,
    requestReports,
    requestLastReport,
    addTeam,
    addProgram,
    editProgram,
    editTeam,
    completeReportRequest,
    cancelReportRequest,
    requestChecklist,
    requestTransfers
  }

})